import React from 'react'
import {
  PieChart,
  Pie,
  Cell,
  Legend,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import Card from '../Card'

interface OrderStats {
  PENDING?: number
  CONFIRMED?: number
  PROCESSING?: number
  SHIPPED?: number
  DELIVERED?: number
  CANCELLED?: number
  RETURNED?: number
  REFUNDED?: number
  [key: string]: number | undefined
}

interface OrdersChartProps {
  stats: OrderStats
  loading?: boolean
}

const COLORS: Record<string, string> = {
  PENDING: '#fbbf24',
  CONFIRMED: '#3b82f6',
  PROCESSING: '#8b5cf6',
  SHIPPED: '#06b6d4',
  DELIVERED: '#10b981',
  CANCELLED: '#ef4444',
  RETURNED: '#ec4899',
  REFUNDED: '#64748b',
}

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pending',
  CONFIRMED: 'Confirmed',
  PROCESSING: 'Processing',
  SHIPPED: 'Shipped',
  DELIVERED: 'Delivered',
  CANCELLED: 'Cancelled',
  RETURNED: 'Returned',
  REFUNDED: 'Refunded',
}

export default function OrdersChart({ stats, loading = false }: OrdersChartProps) {
  if (loading) {
    return (
      <Card title="Orders Overview" subtitle="Distribution of orders by status">
        <div className="h-80 bg-gray-100 animate-pulse rounded-lg"></div>
      </Card>
    )
  }

  if (!stats) {
    return (
      <Card title="Orders Overview" subtitle="Distribution of orders by status">
        <div className="h-80 flex items-center justify-center text-gray-500">
          No order data available
        </div>
      </Card>
    )
  }

  const chartData = Object.entries(stats)
    .filter(([_, value]) => typeof value === 'number' && value > 0)
    .map(([key, value]) => ({
      name: STATUS_LABELS[key] || key,
      value: value || 0,
      status: key,
    }))

  if (chartData.length === 0) {
    return (
      <Card title="Orders Overview" subtitle="Distribution of orders by status">
        <div className="h-80 flex items-center justify-center text-gray-500">
          No order data available
        </div>
      </Card>
    )
  }

  return (
    <Card title="Orders Overview" subtitle="Distribution of orders by status">
      <ResponsiveContainer width="100%" height={400}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={({ name, value }) => `${name}: ${value}`}
            outerRadius={120}
            fill="#6D3A0D"
            dataKey="value"
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[entry.status] || '#6D3A0D'} />
            ))}
          </Pie>
          <Tooltip
            formatter={(value: any) => value}
            contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
          />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </Card>
  )
}
